import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, BookOpen, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import Breadcrumbs from "../components/Breadcrumbs";
import JsonLd from "../components/JsonLd";
import { POSTS as STATIC_GUIDES } from "../lib/blogPosts";

const SITE_URL = "https://pawground.app";

/**
 * /guides — unified listing page. Merges two content sources:
 *   1. Static block-schema content from lib/blogPosts.js (the old /blog
 *      registry — kept after the Blog → Guides merge so existing posts
 *      keep working without a content migration).
 *   2. MongoDB-backed markdown guides via GET /api/guides.
 *
 * Both sources render to the same /guides/{slug} URL. Static guides win
 * if a slug exists in both (defensive — same as GuideDetail.jsx).
 */
const Guides = () => {
  const [apiGuides, setApiGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Dog-Friendly Guides & Tips | Pawground";
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    const prev = metaDesc.getAttribute("content");
    metaDesc.setAttribute(
      "content",
      "Honest, locally-written guides for dog parents — best parks, dog-friendly restaurants, beaches, vets, and travel tips. By Cesar from Pawground."
    );
    return () => {
      if (prev) metaDesc.setAttribute("content", prev);
    };
  }, []);

  useEffect(() => {
    api
      .get("/guides")
      .then((r) => setApiGuides(r.data || []))
      .catch(() => setApiGuides([]))
      .finally(() => setLoading(false));
  }, []);

  // Normalize both sources into a single card shape, dedupe by slug
  // (static wins), and sort by date desc.
  const guides = useMemo(() => {
    const staticAsCards = STATIC_GUIDES.map((p) => ({
      slug: p.slug,
      title: p.title,
      excerpt: p.excerpt,
      author: p.author
        ? `${p.author}${p.authorTitle ? ` — ${p.authorTitle}` : ""}`
        : "Cesar from Pawground",
      published_date: p.publishedAt,
      featured_image: p.coverImage,
      city: null,
      state: null,
    }));
    const staticSlugs = new Set(staticAsCards.map((g) => g.slug));
    const apiAsCards = apiGuides
      .filter((g) => !staticSlugs.has(g.slug))
      .map((g) => ({
        slug: g.slug,
        title: g.title,
        excerpt: g.excerpt || g.meta_description,
        author: g.author,
        published_date: g.published_date,
        featured_image: g.featured_image,
        city: g.city,
        state: g.state,
      }));
    return [...staticAsCards, ...apiAsCards].sort(
      (a, b) => new Date(b.published_date) - new Date(a.published_date)
    );
  }, [apiGuides]);

  // CollectionPage schema lets Google understand this is a curated list
  // of articles (Blog/CollectionPage flavored).
  const listSchema = guides.length
    ? {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Dog-Friendly Guides & Tips",
        url: `${SITE_URL}/guides`,
        isPartOf: { "@type": "WebSite", name: "Pawground", url: SITE_URL },
        hasPart: guides.slice(0, 20).map((g) => ({
          "@type": "Article",
          headline: g.title,
          url: `${SITE_URL}/guides/${g.slug}`,
          datePublished: g.published_date,
          author: { "@type": "Person", name: g.author },
        })),
      }
    : null;

  return (
    <div className="bg-brand-cream min-h-[calc(100vh-4rem)]" data-testid="guides-page">
      {listSchema && <JsonLd schema={listSchema} id="guides-list-schema" />}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 pb-16 md:pt-12">
        <Breadcrumbs items={[{ name: "Guides" }]} />

        <div className="text-center mt-4">
          <span className="inline-flex items-center gap-2 bg-brand-light-green text-brand-forest text-xs font-semibold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full">
            <BookOpen className="w-3.5 h-3.5" />
            Pawground Guides
          </span>
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-brand-ink mt-5 leading-[1.05] tracking-tight"
            data-testid="guides-heading"
          >
            Dog-friendly <span className="text-brand-forest italic">guides &amp; tips</span>
          </h1>
          <p className="text-base sm:text-lg text-brand-ink/70 mt-5 max-w-2xl mx-auto leading-relaxed">
            Locally-written, honest guides for dog parents — best parks, restaurants where dogs are actually welcome, beach etiquette, travel tips, and more.
          </p>
        </div>

        <div className="mt-12 space-y-5" data-testid="guides-list">
          {loading ? (
            <div className="flex items-center gap-2 text-brand-ink/60">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading guides…
            </div>
          ) : guides.length === 0 ? (
            <div className="bg-white rounded-3xl p-8 text-center border border-brand-border-warm">
              <p className="text-brand-ink/60">More guides coming soon.</p>
            </div>
          ) : (
            guides.map((g) => (
              <Link
                key={g.slug}
                to={`/guides/${g.slug}`}
                data-testid={`guide-card-${g.slug}`}
                className="block bg-white rounded-3xl p-6 sm:p-8 border border-brand-border-warm hover:border-brand-forest shadow-warm hover:shadow-warm-lg transition-all hover:-translate-y-0.5 group"
              >
                <div className="flex items-start gap-5">
                  {g.featured_image ? (
                    <img
                      src={g.featured_image}
                      alt={g.title}
                      loading="lazy"
                      className="hidden sm:block shrink-0 w-20 h-20 rounded-2xl object-cover object-top border border-brand-border-warm"
                    />
                  ) : (
                    <div className="hidden sm:flex shrink-0 w-16 h-16 rounded-2xl bg-brand-light-green text-brand-forest items-center justify-center">
                      <BookOpen className="w-8 h-8" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 text-xs text-brand-ink/60 mb-2">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(g.published_date).toLocaleDateString("en-US", {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                      <span className="text-brand-ink/60">· {g.author}</span>
                      {g.city && g.state && (
                        <span className="bg-brand-light-green text-brand-forest text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">
                          {g.city}, {g.state}
                        </span>
                      )}
                    </div>
                    <h2 className="font-heading text-xl sm:text-2xl font-bold text-brand-ink group-hover:text-brand-forest transition-colors leading-tight">
                      {g.title}
                    </h2>
                    <p className="text-sm sm:text-base text-brand-ink/70 mt-2 leading-relaxed line-clamp-3">
                      {g.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-brand-forest group-hover:gap-2 transition-all">
                      Read the guide <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default Guides;
